import {
  CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import cx from "classnames";
import { Redirect } from "../redirect";
import logoImg from "../../assets/logo.png";
import { posthogTrackEvent, NavigationEvents } from "../../assets/posthog";
import { ENavigate, getPageIcon } from "./sidebar.types";
import * as Collapsible from "@radix-ui/react-collapsible";
import { CaretRightIcon } from "@radix-ui/react-icons";
import { useOnboarding } from "../../api/onboarding";
import { automationConfigurationUris } from "@/features/automation-configuration/AutomationConfiguration.config";
import { AppMenu } from "./AppMenu";
import styles from "./styles.module.scss";
import { Badge, Em, Flex, Spinner, Text } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { BackfillStatusAnimation } from "@/routes/onboarding/getting-started/GettingStarted";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { OnboardingModal } from "@/components/sidebar/components/OnboardingModal";
import { StripeRedirectModal } from "@/components/sidebar/components/StripeRedirectModal";
import AddIntegrationDialog from "@/features/integrations/add-integration/AddIntegration";
import { useGetTenantStatus } from "@/v2/entities/tenant-status/TenantStatus.api";
import { useManageTenantStripeApi } from "@/v2/entities/tenant-stripe-management/tenantStripeManagement.api";
import { Tooltip } from "@/components/shared/tooltip";

declare global {
  interface Window {
    quack: any;
  }
}

const unprotected = [
  "/installation-successfull",
  "/welcome",
  "/upskill",
  "/chatbot-sandbox",
  "/playground",
  "/demo",
];

type Page = ENavigate | { root: ENavigate; pages: ENavigate[] };

function isMultiPage(
  page: Page
): page is { root: ENavigate; pages: ENavigate[] } {
  return (
    page != null && page.hasOwnProperty("root") && page.hasOwnProperty("pages")
  );
}

const tooltipMapper: { [x: string]: string } = {
  [ENavigate.HOME]: "Home",
  [ENavigate.ASSESSMENT]: "Topics",
  [ENavigate.GETTING_STARTED]: "Getting Started",
  [ENavigate.SIMULATOR]: "Automation",
  [ENavigate.KNOWLEDGE]: "Articles & Gaps",
  [ENavigate.KNOWLEDGE_EDITOR]: "Snippets",
  [ENavigate.VOC]: "Categories",
  [ENavigate.QUALITY]: "Quality",
  [ENavigate.EVALUATIONS]: "Evaluations",
  [ENavigate.VALIDATIONS]: "Validations",
  [ENavigate.QA_SETTINGS]: "Scorecards",
  [ENavigate.SAMPLING]: "Sampling",
  [ENavigate.COPILOT]: "Copilot",
  [ENavigate.AUTOPILOT]: "Auto-pilot",
  [ENavigate.EXPLORE_INSIGHTS]: "Insights",
  [ENavigate.EXPLORE_TICKETS]: "Tickets",
  [ENavigate.EXPLORE_VIEWS]: "Views",
  [ENavigate.JOURNEYS]: "Journeys",
  [ENavigate.PAGES]: "Pages",
  [ENavigate.EXPLORE_CONVERSATIONS]: "Conversations",
  [ENavigate.KNOWLEDGE_VIEW]: "Articles",
  [ENavigate.KNOWLEDGE_GAPS]: "Knowledge gaps",
  [ENavigate.COPILOT_FEEDBACK]: "Copilot Feedback",
  [ENavigate.SKILLS]: "Skills",
  [ENavigate.ENRICHMENTS]: "Enrichments",
  [ENavigate.TRACKERS]: "Trackers",
  [ENavigate.TRAINING_CENTER]: "Training Center",
  [ENavigate.APOLLO_AGENT]: "Apollo Agent",
  [ENavigate.AUTOMATION]: "Automation",
  [ENavigate.REGRESSION_SETS]: "Regression Sets",
  [ENavigate.REGRESSION_TESTS_V2]: "Simulation Tests",
  [ENavigate.APPROVAL_REQUESTS]: "Approval Requests",
  [ENavigate.GOOD_RESPONSES_LIBRARY]: "Good Feedback Library",
  [ENavigate.NOTICES]: "Notices",
};

// 💡 hint: to hide side bar put the path here 💡
const hideSidebarPaths = [
  `${automationConfigurationUris.root}/:page`,
  "/copilot/:tenantId/ticket/:ticketId",
  "/enrichments/:enrichmentId",
  "/automation/:simulatorId/versions",
  "/automation/:simulatorId/versions/:versionId",
  "/automation/:simulatorId/versions/:versionId/activity",
  "/automation/:simulatorId",
  "/automation/:simulatorId/activity",
  "/automation/:simulatorId/:topicId/live-test",
  "/settings",
  "/settings/data-sources",
  "/settings/members",
  "/settings/agents",
  "/settings/topics",
  "/settings/brands",
  "/settings/billing",
  "/settings/:page",
  "/new-challenge",
  "/automation-center/skills/create",
  "/automation-center/skills/:skillId/edit",
  "/custom-report/:exploreViewId",
  "/training-center/:agentId",
  "/training-center/:agentId/regression-sets/:setId",
  "/training-center/:agentId/regression-sets/:setId/tests/:testId",
  "/training-center/:agentId/regression-sets/:setId/configure",
];

interface AuthenticatedSidebarProps {
  onPageClick: (page?: ENavigate) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

function AuthenticatedSidebar({
  onPageClick,
  isCollapsed,
  onToggleCollapse,
}: AuthenticatedSidebarProps) {
  const { user, logout } = useAuth0();
  const { workspace } = useWorkspace();
  const { data: tenantStatus, refetch: refetchTenantStatus } =
    useGetTenantStatus(workspace);
  const onboarding = useOnboarding();

  const [openCollapsibles, setOpenCollapsibles] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (isCollapsed) {
      setOpenCollapsibles(new Set());
    }
  }, [isCollapsed]);

  const handleCollapsibleChange = useCallback(
    (key: string, isOpen: boolean) => {
      setOpenCollapsibles((prev) => {
        const newSet = new Set(prev);
        if (isOpen) {
          newSet.add(key);
        } else {
          newSet.delete(key);
        }
        return newSet;
      });
    },
    []
  );

  const onboardingModalsValues = useMemo(() => {
    const needsOnboardingData = user?.needsOnboardingData;
    const isNewSignup = user?.isNewSignup;
    const onboardingCompleted = user?.onboardingCompleted;
    return {
      shouldShowOnboardingModal:
        isNewSignup && needsOnboardingData && !onboardingCompleted,
      shouldShowCalendlyModal:
        isNewSignup &&
        !needsOnboardingData &&
        !onboardingCompleted,
      shouldShowIntegrationModal:
        tenantStatus?.status === "onboarding",
      shouldRedirectToPayment:
        tenantStatus?.status === "pending_payment",
    };
  }, [user, tenantStatus]);

  const { createPaymentCheckout } = useManageTenantStripeApi(workspace);
  const [stripeRedirectUrl, setStripeRedirectUrl] = useState<string | null>(
    null
  );

  useEffect(() => {
    const redirectToCheckout = async () => {
      if (onboardingModalsValues.shouldRedirectToPayment) {
        const url = await createPaymentCheckout(
          window.location.origin,
          window.location.origin
        );
        if (url) {
          setStripeRedirectUrl(url);
        }
      }
    };
    redirectToCheckout();
  }, [onboardingModalsValues.shouldRedirectToPayment, createPaymentCheckout]);

  const handleStripeRedirect = useCallback(() => {
    if (stripeRedirectUrl) {
      window.location.href = stripeRedirectUrl;
    }
  }, [stripeRedirectUrl]);

  const hitlApprovalRequestsEnabled = useFlag("hitlApprovalRequests");
  const goodResponsesLibraryEnabled = useFlag("goodResponsesLibrary", false);
  const noticesEnabled = useFlag("notices", false);

  const isArtlistWorkspace = workspace?.toLowerCase() === "artlist";

  const opportunities = useMemo<Page[]>(
    () => [
      ENavigate.TRAINING_CENTER,
      ...(user?.email?.includes("artlist.io") ? [ENavigate.APOLLO_AGENT] : []),
      {
        root: ENavigate.AUTOMATION,
        pages: [
          ENavigate.SKILLS,
          ENavigate.ENRICHMENTS,
          isArtlistWorkspace
            ? ENavigate.REGRESSION_TESTS_V2
            : ENavigate.REGRESSION_SETS,
          ...(goodResponsesLibraryEnabled
            ? [ENavigate.GOOD_RESPONSES_LIBRARY]
            : []),
        ],
      },
      ...([ENavigate.KNOWLEDGE_EDITOR]),
      {
        root: ENavigate.KNOWLEDGE,
        pages: [ENavigate.KNOWLEDGE_VIEW, ENavigate.KNOWLEDGE_GAPS],
      },
      ENavigate.QUALITY,
      ENavigate.TRACKERS,
      ...(hitlApprovalRequestsEnabled ? [ENavigate.APPROVAL_REQUESTS] : []),
      ...(noticesEnabled ? [ENavigate.NOTICES] : []),
    ],
    [
      hitlApprovalRequestsEnabled,
      goodResponsesLibraryEnabled,
      noticesEnabled,
      isArtlistWorkspace,
      user?.email,
    ]
  );

  return (
    <>
      <div className={styles.topContainer}>
        <AppMenu
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
        />

        <div className={styles.pagesContainer}>
          <SidebarPageItem
            page={ENavigate.HOME}
            active={location.pathname === "/"}
            onClick={() => onPageClick(ENavigate.HOME)}
            isCollapsed={isCollapsed}
          />

          {!onboarding.isDone && (
            <GettinStartedSidebarPageItem
              onClick={() => onPageClick(ENavigate.GETTING_STARTED)}
              active={location.pathname.includes(ENavigate.GETTING_STARTED)}
              isCollapsed={isCollapsed}
            />
          )}
          {opportunities.map((page) => {
            if (isMultiPage(page)) {
              const collapsibleKey = page.root;
              const isOpen = openCollapsibles.has(collapsibleKey);

              return (
                <>
                  <Collapsible.Root
                    key={collapsibleKey}
                    className="CollapsibleRoot"
                    open={isOpen}
                    onOpenChange={(open) =>
                      handleCollapsibleChange(collapsibleKey, open)
                    }
                  >
                    <Collapsible.Trigger
                      asChild
                      className={styles.collapseTrigger}
                      disabled={isCollapsed}
                    >
                      <SidebarPageItem
                        page={page.root}
                        onClick={() => {}}
                        active={false}
                        isParent
                        isCollapsed={isCollapsed}
                      />
                    </Collapsible.Trigger>
                    <Collapsible.Content>
                      {page.pages
                        .map((subPage) => {
                          const active = subPage.startsWith(ENavigate.SIMULATOR)
                            ? `${location.pathname}${location.search}`.includes(
                                subPage
                              )
                            : location.pathname.includes(subPage);

                          return (
                            <Fragment key={subPage}>
                              <div
                                style={{ height: "2px", width: "40px" }}
                              ></div>
                              <SidebarPageItem
                                page={subPage}
                                onClick={() => onPageClick(subPage)}
                                active={active}
                                indent={1}
                                isCollapsed={isCollapsed}
                              />
                            </Fragment>
                          );
                        })}
                    </Collapsible.Content>
                  </Collapsible.Root>
                </>
              );
            }

            return (
              <SidebarPageItem
                key={page}
                page={page}
                onClick={() => onPageClick(page)}
                active={location.pathname.includes(page)}
                isCollapsed={isCollapsed}
              />
            );
          })}

          <hr />

          {!isCollapsed && <label>Explore</label>}
          <SidebarPageItem
            page={ENavigate.EXPLORE_TICKETS}
            active={location.pathname.includes(ENavigate.EXPLORE_TICKETS)}
            onClick={() => onPageClick(ENavigate.EXPLORE_TICKETS)}
            isCollapsed={isCollapsed}
          />
          <SidebarPageItem
            page={ENavigate.EXPLORE_CONVERSATIONS}
            active={location.pathname.includes(
              ENavigate.EXPLORE_CONVERSATIONS
            )}
            onClick={() => onPageClick(ENavigate.EXPLORE_CONVERSATIONS)}
            isCollapsed={isCollapsed}
          />

          {
            <SidebarPageItem
              page={ENavigate.EXPLORE_VIEWS}
              active={location.pathname.includes(ENavigate.EXPLORE_VIEWS)}
              onClick={() => onPageClick(ENavigate.EXPLORE_VIEWS)}
              isCollapsed={isCollapsed}
            />
          }

          {/* {journeysGeneralEnabled && (
            <SidebarPageItem
              page={ENavigate.JOURNEYS}
              active={location.pathname.includes(ENavigate.JOURNEYS)}
              onClick={() => onPageClick(ENavigate.JOURNEYS)}
              isCollapsed={isCollapsed}
            />
          )} */}
          {/* {pagesGeneralEnabled && (
            <SidebarPageItem
              page={ENavigate.PAGES}
              active={location.pathname.includes(ENavigate.PAGES)}
              onClick={() => onPageClick(ENavigate.PAGES)}
              isCollapsed={isCollapsed}
            />
          )} */}
        </div>
      </div>
      {onboardingModalsValues.shouldShowCalendlyModal && (
        <Dialog open={true}>
          <Dialog.Content size="large">
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap="4"
              className="p-6"
              height="90vh"
            >
              <Text size="5" weight="bold" align="center">
                Schedule a Product Discovery Call{" "}
                <button style={{ opacity: 0 }} onClick={() => logout()}>
                  logout
                </button>
              </Text>
              <Text size="2" align="center" className="text-gray-500">
                Let's discuss your needs and how we can help you get the most
                out of our platform.
              </Text>
              <div className="w-full h-[600px] grow">
                <iframe
                  src="https://calendly.com/nadav-a6u/cx-2028-quack-ai-discovery-clone-1?from=slack"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  className="rounded-md"
                ></iframe>
              </div>
            </Flex>
          </Dialog.Content>
        </Dialog>
      )}
      {onboardingModalsValues.shouldShowIntegrationModal && (
        <AddIntegrationDialog
          type="TICKET_SYSTEM"
          preSelectedName="zendesk"
          loading={false}
          existingIntegrations={[]}
          onClose={() => {
            refetchTenantStatus();
          }}
          reloadIntegrations={() => {
            return Promise.resolve();
          }}
        />
      )}
      <OnboardingModal
        isOpen={onboardingModalsValues.shouldShowOnboardingModal}
      />
      <StripeRedirectModal
        isOpen={!!stripeRedirectUrl}
        onRedirect={handleStripeRedirect}
      />
    </>
  );
}

export const Sidebar = () => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const subdomain = useMemo(() => {
    return window.location.hostname.split(".")[0];
  }, []);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored === "true";
  });

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem("sidebar-collapsed", String(newValue));
      return newValue;
    });
  }, []);

  useEffect(() => {
    if (user) {
      // window.quack?.hideChat?.();
      window.quack?.identify?.(user);
      // window.quack?.showChat();
    }
  }, [user]);

  const handleClick = useCallback(
    (page: ENavigate = ENavigate.HOME) => {
      // Track navigation with PostHog
      posthogTrackEvent({
        event: NavigationEvents.SIDEBAR_CLICK,
        properties: {
          oldPage: location.pathname.split("/")[1],
          newPage: page,
          fullPath: `/${page}`,
        },
      });

      navigate(`/${page}`);
    },
    [location.pathname, navigate]
  );

  const hide = useMemo(() => {
    return hideSidebarPaths.some((path) => matchPath(path, location.pathname));
  }, [location.pathname]);

  if (unprotected.includes(window.location.pathname) || hide) return null;

  if (isLoading)
    return (
      <div className={styles.container}>
        <div className={styles.loading} />
      </div>
    );

  return (
    <>
      {isAuthenticated ? (
        subdomain === "whitelabel" ? null : (
          <div
            className={cx(styles.container, {
              [styles.collapsed]: isCollapsed,
            })}
          >
            <AuthenticatedSidebar
              onPageClick={handleClick}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />
          </div>
        )
      ) : (
        <div className={cx(styles.container)}>
          <div className={styles.topContainer}>
            <div className={styles.imageContainer}>
              <img
                src={logoImg}
                alt="Quack Logo"
                onClick={() => handleClick()}
              />
            </div>
          </div>
          <Redirect to="/login" />
        </div>
      )}
    </>
  );
};

interface SidebarPageItemProps {
  page: ENavigate;
  active: boolean;
  onClick: () => void;
  indent?: number;
  isParent?: boolean;
  isLegacy?: boolean;
  isCollapsed?: boolean;
}

const SidebarPageItem = ({
  page,
  active,
  onClick,
  indent = 0,
  isParent = false,
  isLegacy = false,
  isCollapsed = false,
}: SidebarPageItemProps) => {
  const pageContent = (
    <>
      {getPageIcon(page, active)}
      {!isCollapsed && (
        <div className={styles.label}>
          {tooltipMapper[page]}
          {isParent && <CaretRightIcon className={styles.collapseBtn} />}
          {isLegacy && (
            <Badge size="1" color="gray" variant="soft" className="ml-2">
              <Em>Legacy</Em>
            </Badge>
          )}
        </div>
      )}
    </>
  );

  const pageItem = (
    <div
      key={page}
      className={cx(styles.pageContainer, {
        [styles.active]: active,
        [styles.collapsed]: isCollapsed,
      })}
      onClick={onClick}
      style={
        {
          "--indent": indent,
        } as CSSProperties
      }
    >
      {pageContent}
    </div>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <Tooltip.Trigger>{pageItem}</Tooltip.Trigger>
        <Tooltip.Content side="right">{tooltipMapper[page]}</Tooltip.Content>
      </Tooltip>
    );
  }

  return pageItem;
};

function GettinStartedSidebarPageItem({
  onClick,
  active,
  isCollapsed,
}: Pick<SidebarPageItemProps, "onClick" | "active" | "isCollapsed">) {
  const { backfillStatus } = useOnboarding();

  if (backfillStatus === "started") {
    const content = (
      <div
        key={ENavigate.GETTING_STARTED}
        className={cx(styles.pageContainer, {
          [styles.active]: active,
          [styles.collapsed]: isCollapsed,
        })}
        onClick={onClick}
        style={
          {
            "--indent": 0,
            overflow: "hidden",
          } as CSSProperties
        }
      >
        <Spinner />
        {!isCollapsed && (
          <div className={styles.label}>
            <BackfillStatusAnimation />
          </div>
        )}
      </div>
    );

    if (isCollapsed) {
      return (
        <Tooltip>
          <Tooltip.Trigger>{content}</Tooltip.Trigger>
          <Tooltip.Content side="right">Getting Started</Tooltip.Content>
        </Tooltip>
      );
    }

    return content;
  }

  return (
    <SidebarPageItem
      page={ENavigate.GETTING_STARTED}
      onClick={onClick}
      active={active}
      isCollapsed={isCollapsed}
    />
  );
}
